import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { ToggleGroup, ToggleItem } from '@/components/ToggleGroup'
import { Form, FormGroup, FormRow } from '@/components/Form'
import { TextInput } from '@/components/inputs/TextInput'
import {
  NotificationBar,
  NotificationBarSubtitle,
  NotificationBarTitle,
} from '@/components/NotificationBar'

/**
 * Which walkthrough the instructions page shows.
 */
export type BookkeepingGrantAccessInstructions = 'quickbooks' | 'xero' | 'other'

export interface BookkeepingGrantAccessSoftwareOption {
  id: string
  label: string
  onSelect: () => unknown
}

export interface BookkeepingGrantAccessLoginLink {
  label: string
  url: string
}

/**
 * The page of the grant access dialog being shown.
 */
export type BookkeepingGrantAccessPage =
  | {
      type: 'SelectSoftware'
      options: BookkeepingGrantAccessSoftwareOption[]
    }
  | {
      type: 'Instructions'
      instructions: BookkeepingGrantAccessInstructions
      loginLink: BookkeepingGrantAccessLoginLink | null
    }

const testId = 'bookkeeping-grant-access-dialog'

export interface BookkeepingGrantAccessProDetails {
  givenName: string
  familyName: string
  email: string
}

export interface BookkeepingGrantAccessDialogProps {
  showing: boolean
  page: BookkeepingGrantAccessPage
  isClientPerspective: boolean
  proDetails: BookkeepingGrantAccessProDetails | null
  otherSoftwareName: string
  otherSoftwareField: ReactNode
  isConfirmPending: boolean
  onClose: () => unknown
  onBack: () => unknown
  onCopy: (value: string) => unknown
  onConfirm: () => unknown
}

export function BookkeepingGrantAccessDialog({
  showing,
  page,
  onClose,
  ...pageProps
}: BookkeepingGrantAccessDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={pageProps.isConfirmPending ? undefined : onClose}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader withCloseButton />
      {page.type === 'SelectSoftware' ? (
        <SelectSoftwarePage
          options={page.options}
          isClientPerspective={pageProps.isClientPerspective}
          onClose={onClose}
        />
      ) : (
        <InstructionsPage
          instructions={page.instructions}
          loginLink={page.loginLink}
          {...pageProps}
        />
      )}
    </ModalSheet>
  )
}

interface SelectSoftwarePageProps {
  options: BookkeepingGrantAccessSoftwareOption[]
  isClientPerspective: boolean
  onClose: () => unknown
}

/**
 * Lets the user pick the platform the client keeps their books in.
 */
function SelectSoftwarePage({
  options,
  isClientPerspective,
  onClose,
}: SelectSoftwarePageProps) {
  return (
    <>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            {isClientPerspective
              ? 'Share access to your books'
              : `Share access to the client's books`}
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            {isClientPerspective
              ? 'Select your bookkeeping account to get started.'
              : `Select the client's bookkeeping account to get started.`}
          </ModalSheetContentDescription>
          {/*
           * Nothing is ever left `toggled` here - picking a platform navigates
           * straight to its instructions. ToggleItem is used for the click,
           * keyboard and focus handling it brings with it.
           */}
          <ToggleGroup stretch>
            {options.map((option) => (
              <ToggleItem
                key={option.id}
                onChange={option.onSelect}
                testId={`${testId}-software-${option.id}`}
              >
                {option.label}
              </ToggleItem>
            ))}
          </ToggleGroup>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            testId={`${testId}-close-button`}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

interface InstructionsPageProps extends Omit<
  BookkeepingGrantAccessDialogProps,
  'showing' | 'page' | 'onClose'
> {
  instructions: BookkeepingGrantAccessInstructions
  loginLink: BookkeepingGrantAccessLoginLink | null
}

/**
 * Walks the user through granting the Pro access, and collects the
 * confirmation that they did.
 */
function InstructionsPage({
  instructions,
  loginLink,
  isClientPerspective: isClient,
  proDetails,
  otherSoftwareName,
  otherSoftwareField,
  isConfirmPending,
  onBack,
  onCopy,
  onConfirm,
}: InstructionsPageProps) {
  return (
    <>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            {instructions === 'other'
              ? isClient
                ? 'Select your bookkeeping software'
                : `Select the client's bookkeeping software`
              : isClient
                ? 'Add your Pro to your books'
                : `Add the Pro to the client's books`}
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            <PlatformInstructions
              instructions={instructions}
              isClient={isClient}
            />
          </ModalSheetContentDescription>

          <Form>
            {instructions === 'other' && (
              <>
                <FormRow>{otherSoftwareField}</FormRow>
                <FormRow>
                  <OtherSoftwareInstructions
                    softwareName={otherSoftwareName}
                    isClient={isClient}
                  />
                </FormRow>
              </>
            )}

            {proDetails ? (
              <>
                <FormRow>
                  <CopyTextField
                    label="First name"
                    value={proDetails.givenName}
                    onCopy={() => onCopy(proDetails.givenName)}
                    testId={`${testId}-copy-given-name-button`}
                  />
                </FormRow>
                <FormRow>
                  <CopyTextField
                    label="Last name"
                    value={proDetails.familyName}
                    onCopy={() => onCopy(proDetails.familyName)}
                    testId={`${testId}-copy-family-name-button`}
                  />
                </FormRow>
                <FormRow>
                  <CopyTextField
                    label="Email address"
                    value={proDetails.email}
                    onCopy={() => onCopy(proDetails.email)}
                    testId={`${testId}-copy-email-button`}
                  />
                </FormRow>
              </>
            ) : (
              <FormRow>
                <MissingProDetails isClient={isClient} />
              </FormRow>
            )}

            {!isClient && (
              <FormRow>
                <NotificationBar testId={`${testId}-admin-note`}>
                  <NotificationBarSubtitle>
                    Only confirm once the client has actually shared access. The
                    client will not be notified that you did this on their
                    behalf.
                  </NotificationBarSubtitle>
                </NotificationBar>
              </FormRow>
            )}
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Back"
            variant="default"
            onPress={onBack}
            isDisabled={isConfirmPending}
            testId={`${testId}-back-button`}
          />
          {loginLink && (
            <Button
              label={`Open ${loginLink.label}`}
              variant="default"
              href={loginLink.url}
              target="_blank"
              testId={`${testId}-open-platform-button`}
            />
          )}
          <Button
            label="Access provided"
            variant="primary"
            onPress={onConfirm}
            isPending={isConfirmPending}
            isDisabled={isConfirmPending || !proDetails}
            testId={`${testId}-access-provided-button`}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

interface PlatformInstructionsProps {
  instructions: BookkeepingGrantAccessInstructions
  isClient: boolean
}

/**
 * The steps for granting access in the selected platform.
 */
function PlatformInstructions({
  instructions,
  isClient,
}: PlatformInstructionsProps) {
  switch (instructions) {
    case 'quickbooks':
      return (
        <>
          {isClient ? (
            <QuickBooksClientInstructions />
          ) : (
            <QuickBooksAdminInstructions />
          )}
          <br />
          <ConfirmationHint isClient={isClient} />
        </>
      )
    case 'xero':
      return (
        <>
          {isClient ? <XeroClientInstructions /> : <XeroAdminInstructions />}
          <br />
          <ConfirmationHint isClient={isClient} />
        </>
      )
    case 'other':
      return (
        <>
          {isClient ? (
            <OtherSoftwareClientInstructions />
          ) : (
            <OtherSoftwareAdminInstructions />
          )}
        </>
      )
  }
}

interface ConfirmationHintProps {
  isClient: boolean
}

/**
 * Points the user at the button that completes the step.
 */
function ConfirmationHint({ isClient }: ConfirmationHintProps) {
  return (
    <p>
      Once granted, click <b>Access provided</b> so{' '}
      {isClient ? 'your Pro' : 'the Pro'} can get started.
    </p>
  )
}

interface OtherSoftwareInstructionsProps {
  softwareName: string
  isClient: boolean
}

/**
 * The steps for granting access in a software we have no walkthrough for.
 */
function OtherSoftwareInstructions({
  softwareName,
  isClient,
}: OtherSoftwareInstructionsProps) {
  return (
    <p>
      In <b>{softwareName}</b>,{' '}
      {isClient
        ? 'invite your Pro as a user with accounting access'
        : 'the client should invite the Pro as a user with accounting access'}{' '}
      using the details below. Then click <b>Access provided</b> so{' '}
      {isClient ? 'your Pro' : 'the Pro'} can get started.
    </p>
  )
}

interface CopyTextFieldProps {
  label: string
  value: string
  onCopy: () => unknown
  testId?: string
}

/**
 * A read-only field with a button that copies its value.
 */
function CopyTextField({ label, value, onCopy, testId }: CopyTextFieldProps) {
  return (
    <FormGroup label={label}>
      <TextInput
        value={value}
        readOnly
        button={
          <Button
            label="Copy"
            variant="text"
            onPress={onCopy}
            testId={testId}
          />
        }
      />
    </FormGroup>
  )
}

interface MissingProDetailsProps {
  isClient: boolean
}

/**
 * Shown when we could not resolve the Pro's details.
 */
function MissingProDetails({ isClient }: MissingProDetailsProps) {
  return (
    <NotificationBar type="error" testId={`${testId}-missing-pro-details`}>
      <NotificationBarTitle>
        We couldn't load {isClient ? 'your' : 'the'} Pro's details
      </NotificationBarTitle>
      <NotificationBarSubtitle>
        Please refresh the page and try again. If the problem persists, let us
        know in the job conversation.
      </NotificationBarSubtitle>
    </NotificationBar>
  )
}

/**
 * QuickBooks instructions for clients.
 */
function QuickBooksClientInstructions() {
  return (
    <p>
      Go to <b>Manage users</b> in your QuickBooks settings. Under the{' '}
      <b>Accounting firms</b> tab, click <b>Invite</b>. Use the information
      below to complete the necessary fields to add us to your books.
    </p>
  )
}

/**
 * QuickBooks instructions for admins.
 */
function QuickBooksAdminInstructions() {
  return (
    <p>
      The client should go to <b>Manage users</b> in their QuickBooks settings.
      Under the <b>Accounting firms</b> tab, click <b>Invite</b>. Use the
      information below to complete the necessary fields to add us to their
      books.
    </p>
  )
}

/**
 * Xero instructions for clients.
 */
function XeroClientInstructions() {
  return (
    <p>
      Click on your organization name in Xero and select <b>Settings</b>, then
      click <b>Users</b>. Click <b>Invite a user</b> and enter your Pro's
      details below. Under Permissions, check <b>Business and accounting</b>,
      choose the <b>Advisor</b> role, then click <b>Send invite</b>.
    </p>
  )
}

/**
 * Xero instructions for admins.
 */
function XeroAdminInstructions() {
  return (
    <p>
      The client should click their organization name in Xero and select{' '}
      <b>Settings</b>, then click <b>Users</b>. Click <b>Invite a user</b> and
      enter the Pro's details below. Under Permissions, check{' '}
      <b>Business and accounting</b>, choose the <b>Advisor</b> role, then click{' '}
      <b>Send invite</b>.
    </p>
  )
}

/**
 * Other software instructions for clients.
 */
function OtherSoftwareClientInstructions() {
  return (
    <p>
      Choose the software you use, then add your Pro with the details below so
      they can get started right away.
    </p>
  )
}

/**
 * Other software instructions for admins.
 */
function OtherSoftwareAdminInstructions() {
  return (
    <p>
      Choose the software the client uses, then add the Pro with the details
      below so they can get started right away.
    </p>
  )
}
